// src/components/Navigation.jsx
import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Tabs, Tab, Box, AppBar, Toolbar, Typography, IconButton, Tooltip,
  Menu, MenuItem, Chip, Button, Drawer, List, ListItem, ListItemButton,
  ListItemIcon, ListItemText, Divider, useMediaQuery, useTheme,
} from '@mui/material';
import {
  Brightness4 as DarkIcon,
  Brightness7 as LightIcon,
  Palette as PaletteIcon,
  Person as DesignerIcon,
  Explore as WorldsIcon,
  Schema as OntologyIcon,
  Rule as RuleIcon,
  Build as ServicesIcon,
  SportsEsports as GameIcon,
  RocketLaunch as AssetIcon,
  AccountCircle,
  Logout,
  Menu as MenuIcon,
  Close as CloseIcon,
  People as PeopleIcon,
  VpnKey as KratosIcon,
} from '@mui/icons-material';
import { useThemeContext } from '../ThemeContext';
import { logoutUser } from '../store/thunks/authThunks';
import LoginModal from './auth/LoginModal';
import UserAvatar from './common/UserAvatar';
import { isAdmin } from '../utils/accessControl';

const TABS = [
  { label: 'Дизайнер', path: '/designer', icon: <DesignerIcon sx={{ fontSize: 18 }} />, tooltip: 'Создание и редактирование персонажей' },
  { label: 'Миры', path: '/worlds', icon: <WorldsIcon sx={{ fontSize: 18 }} />, tooltip: 'Исследование игровых миров' },
  { label: 'Ангар', path: '/assets', icon: <AssetIcon sx={{ fontSize: 18 }} />, tooltip: 'Управление кораблями и структурами' },
  { label: 'Онтологии', path: '/ontologys', icon: <OntologyIcon sx={{ fontSize: 18 }} />, tooltip: 'Редактор онтологий' },
  { label: 'Правила', path: '/rules', icon: <RuleIcon sx={{ fontSize: 18 }} />, tooltip: 'Управление правилами' },
  { label: 'Сервисы', path: '/services', icon: <ServicesIcon sx={{ fontSize: 18 }} />, tooltip: 'Тестирование сервисов' },
  { label: 'Игра', path: '/game/cabinet', icon: <GameIcon sx={{ fontSize: 18 }} />, tooltip: 'Основной игровой интерфейс' },
];

const getActiveTabIndex = (pathname) => {
  if (pathname.startsWith('/designer')) return 0;
  if (pathname.startsWith('/worlds')) return 1;
  if (pathname.startsWith('/assets')) return 2;
  if (pathname.startsWith('/ontologys')) return 3;
  if (pathname.startsWith('/rules')) return 4;
  if (pathname.startsWith('/services')) return 5;
  if (pathname.startsWith('/game')) return 6;
  return -1;
};

const Navigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const {
    darkMode,
    toggleDarkMode,
    colorScheme,
    changeColorScheme,
    colorSchemes: availableSchemes,
    getColorSchemeName,
    colorSchemesData,
  } = useThemeContext();

  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const auth = useSelector((state) => state.auth);

  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [colorMenuAnchor, setColorMenuAnchor] = useState(null);
  const [authMenuAnchor, setAuthMenuAnchor] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const activeIndex = getActiveTabIndex(location.pathname);

  const handleColorMenuOpen = (e) => setColorMenuAnchor(e.currentTarget);
  const handleColorMenuClose = () => setColorMenuAnchor(null);
  const handleColorSchemeSelect = (scheme) => {
    changeColorScheme(scheme);
    handleColorMenuClose();
  };

  const handleAuthMenuOpen = (e) => setAuthMenuAnchor(e.currentTarget);
  const handleAuthMenuClose = () => setAuthMenuAnchor(null);

  const handleLogout = async () => {
    try {
      await dispatch(logoutUser()).unwrap();
      handleAuthMenuClose();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      handleAuthMenuClose();
      navigate('/');
    }
  };

  const handleProfile = () => { handleAuthMenuClose(); navigate('/profile'); };

  const handleAdminUsers = () => {
    handleAuthMenuClose();
    navigate('/admin/users');
  };

  const handleAdminKratos = () => {
    handleAuthMenuClose();
    navigate('/admin/kratos');
  };

  const handleLogoClick = () => navigate('/');
  const handleLoginOpen = () => setLoginModalOpen(true);
  const handleLoginClose = () => setLoginModalOpen(false);

  const handleNavClick = (path) => {
    navigate(path);
    setDrawerOpen(false);
  };

  const userEmail =
    user?.identity?.traits?.email || user?.traits?.email || user?.email || 'Пользователь';

  const userName = auth?.access?.display_name || userEmail;

  const admin = isAdmin(auth);

  // ===== Мобильное меню (Drawer) =====
  const mobileDrawer = (
    <Drawer
      anchor="left"
      open={drawerOpen}
      onClose={() => setDrawerOpen(false)}
      PaperProps={{ sx: { width: 260 } }}
    >
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GameIcon sx={{ fontSize: 24 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>SIM - UEF</Typography>
        </Box>
        <IconButton size="small" onClick={() => setDrawerOpen(false)}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Divider />
      <List dense>
        {TABS.map((tab, idx) => (
          <ListItem key={tab.path} disablePadding>
            <ListItemButton
              selected={activeIndex === idx}
              onClick={() => handleNavClick(tab.path)}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>{tab.icon}</ListItemIcon>
              <ListItemText primary={tab.label} primaryTypographyProps={{ fontSize: '0.85rem' }} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      {admin && isAuthenticated && (
        <>
          <Divider />
          <List dense>
            <ListItem disablePadding>
              <ListItemButton
                selected={location.pathname.startsWith('/admin/users')}
                onClick={() => handleNavClick('/admin/users')}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <PeopleIcon sx={{ fontSize: 18 }} />
                </ListItemIcon>
                <ListItemText
                  primary="Пользователи и роли"
                  primaryTypographyProps={{ fontSize: '0.85rem' }}
                />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton
                selected={location.pathname.startsWith('/admin/kratos')}
                onClick={() => handleNavClick('/admin/kratos')}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <KratosIcon sx={{ fontSize: 18 }} />
                </ListItemIcon>
                <ListItemText
                  primary="Kratos identities"
                  primaryTypographyProps={{ fontSize: '0.85rem' }}
                />
              </ListItemButton>
            </ListItem>
          </List>
        </>
      )}
    </Drawer>
  );

  return (
    <>
      <AppBar
        position="fixed"
        elevation={2}
        sx={{ zIndex: (t) => t.zIndex.drawer + 1 }}
      >
        <Toolbar sx={{ minHeight: { xs: 48, md: 52 }, px: { xs: 1, md: 2 } }}>
          {/* Burger (mobile) */}
          {isMobile && (
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => setDrawerOpen(true)}
              sx={{ mr: 0.5 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Logo */}
          <Tooltip title="На главную страницу" arrow>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mr: { xs: 1, md: 2 },
                cursor: 'pointer',
                '&:hover': { opacity: 0.8 },
              }}
              onClick={handleLogoClick}
            >
              <GameIcon sx={{ mr: 0.5, fontSize: { xs: 22, md: 24 } }} />
              <Typography
                variant="h6"
                component="div"
                sx={{ fontWeight: 'bold', fontSize: { xs: '1rem', md: '1.1rem' } }}
              >
                SIM - UEF
              </Typography>
            </Box>
          </Tooltip>

          {/* Tabs (desktop) */}
          {!isMobile && (
            <Tabs
              value={activeIndex === -1 ? false : activeIndex}
              textColor="inherit"
              sx={{
                '& .MuiTab-root': {
                  minHeight: 52,
                  minWidth: 'auto',
                  px: 1.5,
                  mx: 0.25,
                  fontSize: '0.75rem',
                  textTransform: 'none',
                },
                '& .MuiTab-iconWrapper': { mr: 0.5 },
              }}
            >
              {TABS.map((tab, index) => (
                <Tooltip key={tab.path} title={tab.tooltip} arrow>
                  <Tab
                    icon={tab.icon}
                    label={tab.label}
                    component={Link}
                    to={tab.path}
                    iconPosition="start"
                    sx={{
                      fontWeight: activeIndex === index ? 'bold' : 'normal',
                    }}
                  />
                </Tooltip>
              ))}
            </Tabs>
          )}

          <Box sx={{ flexGrow: 1 }} />

          {/* Right side icons */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title="Цветовая схема">
              <IconButton
                color="inherit"
                size="small"
                onClick={handleColorMenuOpen}
                sx={{ '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
              >
                <PaletteIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Menu
              anchorEl={colorMenuAnchor}
              open={Boolean(colorMenuAnchor)}
              onClose={handleColorMenuClose}
              PaperProps={{ sx: { mt: 1.5, '& .MuiMenuItem-root': { minWidth: 160, py: 1 } } }}
            >
              <MenuItem disabled>
                <Typography variant="subtitle2" color="text.secondary">Цветовая схема:</Typography>
              </MenuItem>
              {availableSchemes.map((scheme) => (
                <MenuItem
                  key={scheme}
                  selected={colorScheme === scheme}
                  onClick={() => handleColorSchemeSelect(scheme)}
                  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                    {getColorSchemeName(scheme)}
                  </Box>
                  <Chip
                    size="small"
                    sx={{
                      backgroundColor: colorSchemesData?.[scheme]?.[darkMode ? 'dark' : 'light']?.main || '#ccc',
                      width: 20, height: 20, minWidth: 20,
                      '& .MuiChip-label': { display: 'none' },
                    }}
                  />
                </MenuItem>
              ))}
            </Menu>

            <Tooltip title={darkMode ? 'Светлая тема' : 'Тёмная тема'}>
              <IconButton
                color="inherit"
                size="small"
                onClick={toggleDarkMode}
                sx={{ '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
              >
                {darkMode ? <LightIcon fontSize="small" /> : <DarkIcon fontSize="small" />}
              </IconButton>
            </Tooltip>

            {isAuthenticated ? (
              <>
                <Tooltip title="Профиль пользователя">
                  <Chip
                    avatar={
                      <UserAvatar
                        access={auth?.access}
                        size="xs"
                        showTooltip={false}
                      />
                    }
                    label={userName}
                    onClick={handleAuthMenuOpen}
                    variant="outlined"
                    size="small"
                    sx={{
                      color: 'inherit',
                      borderColor: 'rgba(255,255,255,0.3)',
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
                      '& .MuiChip-avatar': {
                        width: 20,
                        height: 20,
                        ml: 0.5,
                      },
                      '& .MuiChip-label': {
                        maxWidth: { xs: 80, md: 150 },
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: { xs: '0.65rem', md: '0.75rem' },
                      },
                    }}
                  />
                </Tooltip>

                <Menu
                  anchorEl={authMenuAnchor}
                  open={Boolean(authMenuAnchor)}
                  onClose={handleAuthMenuClose}
                  PaperProps={{ sx: { mt: 1.5, minWidth: 240, '& .MuiMenuItem-root': { py: 1 } } }}
                >
                  <MenuItem disabled sx={{ opacity: '1 !important' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <UserAvatar access={auth?.access} size="md" />
                      <Box>
                        <Typography variant="subtitle2" noWrap>
                          {userName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {userEmail}
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>

                  <Divider sx={{ my: 0.5 }} />

                  <MenuItem onClick={handleProfile}>
                    <AccountCircle fontSize="small" sx={{ mr: 1 }} /> Профиль
                  </MenuItem>

                  {admin && <Divider sx={{ my: 0.5 }} />}

                  {admin && (
                    <MenuItem onClick={handleAdminUsers}>
                      <PeopleIcon fontSize="small" sx={{ mr: 1 }} />
                      Пользователи и роли
                    </MenuItem>
                  )}

                  {admin && (
                    <MenuItem onClick={handleAdminKratos}>
                      <KratosIcon fontSize="small" sx={{ mr: 1 }} />
                      Kratos identities
                    </MenuItem>
                  )}

                  <Divider sx={{ my: 0.5 }} />

                  <MenuItem onClick={handleLogout}>
                    <Logout fontSize="small" sx={{ mr: 1 }} /> Выйти
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <Button
                color="inherit"
                size="small"
                onClick={handleLoginOpen}
                startIcon={<AccountCircle fontSize="small" />}
                sx={{ ml: 0.5, fontSize: '0.75rem', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
              >
                Войти
              </Button>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {isMobile && mobileDrawer}

      <Toolbar sx={{ minHeight: { xs: 48, md: 52 } }} />
      <LoginModal open={loginModalOpen} onClose={handleLoginClose} />
    </>
  );
};

export default Navigation;
